import React, {useEffect, useState} from "react";
import ConstructionSectionParser from "./../jobCodeAssignment/ConstructionSectionParser";
import ProductAssignment from "./../productAssignment/ProductAssignment";
import OGLAssignment from "./../oglAssignment/OGLAssignment";
import DeviToOGLAssingment from "../deviToOGLAssignment/DeviToOGLAssingment";
import FinalApproval from "./../finalApproval/FinalApproval";
import {Button, message, Modal, Select, Steps, Table, Tooltip} from "antd";
import {DeviData} from "../../../../types/DeviData";
import {JobCode} from "../../../../types/JobCodes";
import './CreateProjectWizard.css';
import {ExclamationCircleOutlined, InfoCircleOutlined, LeftOutlined, RightOutlined, SaveOutlined} from "@ant-design/icons";
import {House} from "../../../../types/House";
import axios from "axios";
import {getConfig} from "../../../../config";
import {AssignmentData} from "../../../../types/AssignmentData";
import {ViewType} from "../../../../types/ViewType";
import ProductApproval
    from "../../abacusToNetworkTransfer/transferProjectView/transferProjectProjectViews/ProductApproval";

const { Step } = Steps;

const CreateProjectWizard: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [deviData, setDeviData] = useState<DeviData[]>([]);
    const [filteredData, setFilteredData] = useState<DeviData[]>([]);
    const [jobCodes, setJobCodes] = useState<JobCode[]>([]);
    const [assignmentData, setAssignmentData] = useState<AssignmentData[]>([]);
    const { API_BASE_URL } = getConfig();
    const [availableProjects, setAvailableProjects] = useState<string[]>([]);
    const [selectedProject, setSelectedProject] = useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [isGrouped, setIsGrouped] = useState(false); // <- Hochziehen
    const [groupedData, setGroupedData] = useState<any[]>([]); // falls nötig für payload
    const [houses, setHouses] = useState<House[]>([
        {
            id: 1,
            name: "Haus 1",
            supplement: "0",
            apartments: 0,
            type: "Wohngebäude",
            sections: { Innen: [], Aussen: [], Umgebung: [] },
            dates: { Bezug: null, Geruest: null, VormontageAussen: null, VormontageInnen: null },
        },
    ]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.get<string[]>(`${API_BASE_URL}/getNetworkPlanConfigurableProjectData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectManagerId=273`);
                setAvailableProjects(response.data);
            } catch (error) {
                message.error("Fehler beim Laden der Projekte");
            }
        };
        fetchProjects();
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchData();
            fetchJobCodes(selectedProject);
            loadPersistedData();
        }
    }, [selectedProject]);

    const showValidationError = (title: string, errorMessage: string) => {
        const cleanedMessage = errorMessage
            .split("\n")
            .filter((line) => line.trim() !== "")
            .map((line, index) => <p key={index} style={{ margin: "4px 0", whiteSpace: "pre-line" }}>{line}</p>);

        Modal.error({
            title,
            icon: <ExclamationCircleOutlined />,
            content: <div>{cleanedMessage}</div>,
            okText: "Schliessen"
        });
    };

    const showValidationErrorTable = (title: string, errorList: { cardName: string; error: string }[]) => {
        const groupedErrorsMap: Record<string, string[]> = {};
        for (const entry of errorList) {
            if (!groupedErrorsMap[entry.cardName]) {
                groupedErrorsMap[entry.cardName] = [];
            }
            groupedErrorsMap[entry.cardName].push(entry.error);
        }
        const groupedErrors = Object.entries(groupedErrorsMap).map(([cardName, errors]) => ({
            cardName,
            error: errors.join('\n')
        }));
        Modal.error({
            title,
            icon: <ExclamationCircleOutlined />,
            content: (
                <Table
                    size="small"
                    dataSource={groupedErrors}
                    columns={[
                        {
                            title: "Bezeichner",
                            dataIndex: "cardName",
                            key: "cardName",
                            width: '60%',
                            render: text => (<div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>)
                        },
                        {
                            title: "Fehler",
                            dataIndex: "error",
                            key: "error",
                            width: '40%',
                            render: text => (<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>)
                        }
                    ]}
                    pagination={false}
                    rowKey={(_, index) => index!!.toString()}
                    scroll={{ y: 400 }}
                />
            ),
            width: 1000,
            okText: "Schliessen"
        });
    };

    const hasInvalidData = groupedData.some(item => item.state === "invalid");

    const validateStep = async (stepIndex: number): Promise<boolean> => {
        if (stepIndex === 0) {
            if (!selectedProject) {
                showValidationError("Validierungsfehler", "Bitte wähle ein Projekt aus.");
                return false;
            }
        }
        if (stepIndex === 1) {
            if (jobCodes.length === 0) {
                showValidationError("Validierungsfehler", "❌ Keine Produkte vorhanden.");
                return false;
            }
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferJobCodeDefinition`, {
                    projectNo: selectedProject,
                    jobCodes: jobCodes
                });

                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "❌ Validierung der Produkte fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "❌ Fehler beim Validieren der Produkte.");
                return false;
            }
        }
        if (stepIndex === 2) {
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferDeviAssignment`, {
                    projectNo: selectedProject,
                    deviData: deviData
                });
                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "Validierung der Devi-Daten fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "Fehler beim Validieren der Devi-Daten.");
                return false;
            }
        }
        if (stepIndex === 3) {
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferProductAssignment`, {
                    projectNo: selectedProject,
                    jobCodes: jobCodes,
                });
                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "Validierung der Produktkonfiguration fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "Fehler beim Validieren der Produktkonfiguration.");
                return false;
            }
        }
        if (stepIndex === 4) {
            try {
                const response = await axios.post(`${API_BASE_URL}/validateTransferHousesAssignment`, {
                    projectNo: selectedProject,
                    jobCodes: jobCodes,
                    houses: houses,
                });
                if (response.status !== 200) {
                    showValidationErrorTable("Validierungsfehler", response.data.message || "Validierung der Produktkonfiguration fehlgeschlagen.");
                    return false;
                }
            } catch (error: any) {
                showValidationErrorTable("Validierungsfehler", error.response?.data || "Fehler beim Validieren der Produktkonfiguration.");
                return false;
            }
        }
        return true;
    }


    const loadPersistedData = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/getPersistedLocalData`, {
                params: { projectNo: selectedProject }
            });
            if (response.status === 200 && response.data) {
                const { assignmentData, houses, deviData, jobCodes } = response.data;
                setAssignmentData(assignmentData || []);
                setHouses(houses || [
                    {
                        id: 1,
                        name: "Haus 1",
                        supplement: "0",
                        sections: { Innen: [], Aussen: [], Umgebung: [] },
                        dates: { Bezug: null, Geruest: null, VormontageAussen: null, VormontageInnen: null },
                    },
                ]);
                setDeviData(deviData || []);
                setFilteredData(deviData || []);
                setJobCodes(jobCodes || []);
                if (
                    (assignmentData && assignmentData.length > 0) ||
                    (houses && houses.length > 0) ||
                    (deviData && deviData.length > 0) ||
                    (jobCodes && jobCodes.length > 0)
                ) {
                    message.success('Gespeicherte Daten wurden erfolgreich geladen.');
                }
            }
        } catch (error) {
            console.error("Fehler beim Laden der gespeicherten Daten:", error);
        }
    };

    const handleNext = async () => {
        setIsLoading(true);
        const isValid = await validateStep(currentStep);
        setIsLoading(false);
        if (!isValid) return;
        if (currentStep === 1) {
            filterJobCodesByDeviData();
        }
        if (currentStep === steps.length - 1) {
            handleSave();
        } else {
            setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
        }
    };


    const handleSave = async () => {
        try {
            const response = await axios.post(`${API_BASE_URL}/validateTransferFinalApproval`, {
                data: groupedData,
                project: selectedProject,
                assignmentData,
                houses,
                deviData,
                jobCodes
            });
            if (response.status !== 200) {
                showValidationErrorTable("Validierungsfehler (Abschlussprüfung)", response.data.message || "Validierung beim Abschluss fehlgeschlagen.");
                return;
            }
        } catch (error: any) {
            showValidationErrorTable("Validierungsfehler (Abschlussprüfung)", error.response?.data || "Fehler beim Validieren der Abschlussdaten.");
            return;
        }
        Modal.confirm({
            width: 600,
            title: "Willst Du die Daten speichern?",
            content: "Die Änderungen werden gespeichert.",
            okText: "Ja",
            cancelText: "Abbrechen",
            onOk: async () => {
                if (!isGrouped) {
                    message.warning("Bitte zuerst gruppieren, bevor gespeichert wird.");
                    return;
                }
                setIsLoading(true);
                try {
                    const response = await axios.post(
                        `${API_BASE_URL}/createOrderCardsForNetworkPlan`,
                        {
                            data: groupedData,
                            project: selectedProject,
                            assignmentData,
                            houses,
                            deviData,
                            jobCodes
                        }
                    );
                    if (response.status === 200) {
                        message.success("Produkt-Daten erfolgreich gespeichert.");
                    } else {
                        message.error("Fehler beim Speichern: Unbekannter Fehler.");
                    }
                } catch (error: any) {
                    console.error("Error saving data:", error);
                    const errorMessage =
                        error.response?.data || "Fehler beim Speichern: Unbekannter Fehler.";
                    Modal.error({
                        title: "Fehler beim Speichern",
                        content: (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: errorMessage,
                                }}
                            />
                        ),
                    });
                } finally {
                    setIsLoading(false);
                    setCurrentStep(0);
                    onClose();
                }
            }
        });
    };

    const filterJobCodesByDeviData = () => {
        const assignedJobCodeIds = new Set(deviData.map(d => d.jobCode).filter(Boolean));
        const updatedJobCodes = jobCodes.map(job => ({
            ...job,
            _hasAssignment: assignedJobCodeIds.has(job.alias)
        }));
        const removed = updatedJobCodes.filter(j => !j._hasAssignment);
        const filtered = updatedJobCodes.filter(j => j._hasAssignment);
        if (removed.length > 0) {
            Modal.info({
                title: "Nicht relevante Produkte",
                okText: "Schliessen",
                content: (
                    <Table
                        dataSource={removed}
                        size="small"
                        rowKey={(record) => record.alias}
                        columns={[
                            { title: "Code", dataIndex: "alias", key: "alias" },
                            { title: "Bezeichner", dataIndex: "descriptionSpez", key: "descriptionSpez" }
                        ]}
                        pagination={false}
                    />
                ),
                width: 600,
                onOk: () => {
                    setJobCodes(filtered.map(({ _hasAssignment, ...rest }) => rest));
                }
            });
        } else {
            setJobCodes(filtered.map(({ _hasAssignment, ...rest }) => rest));
        }
    };

    const [selectedHouseId, setSelectedHouseId] = useState(1);
    const steps = [
        {
            title: "Projekt wählen",
            content: (
                <Select
                    showSearch
                    placeholder="Bitte Projekt auswählen"
                    style={{ width: 300 }}
                    value={selectedProject}
                    onChange={setSelectedProject}
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                        const label = option?.children as React.ReactNode;
                        if (typeof label === 'string') {
                            return label.toLowerCase().includes(input.toLowerCase());
                        }
                        return false;
                    }}
                >
                    {availableProjects
                        .sort((a, b) => b.localeCompare(a)) // Absteigend sortiert
                        .map(p => (
                            <Select.Option key={p} value={p}>{p}</Select.Option>
                        ))
                    }
                </Select>
            ),
        },
        {
            title: "Produkte",
            content: (
                <ProductApproval
                    selectedProject={selectedProject!}
                    jobCodes={jobCodes}
                    setJobCodes={setJobCodes}
                />
            )
        },
        {
            title: "Devis konfigurieren",
            content: <ConstructionSectionParser
                deviData={deviData}
                setDeviData={setDeviData}
                filteredData={filteredData}
                setFilteredData={setFilteredData}
                jobCodes={jobCodes}
                selectedProject={selectedProject}
                setJobCodes={setJobCodes}
                viewType={ViewType.NEW}/>,
        },
        {
            title: "Produkte konfigurieren",
            content: <ProductAssignment
                jobCodes={jobCodes}
                setJobCodes={setJobCodes} />,
        },
        {
            title: "OGL konfigurieren",
            content: (
                <OGLAssignment
                    houses={houses}
                    setHouses={setHouses}
                    selectedHouseId={selectedHouseId}
                    setSelectedHouseId={setSelectedHouseId}
                    selectedProject={selectedProject}
                />
            ),
        },
        {
            title: "Mengen zuweisen",
            content: <DeviToOGLAssingment
                houses={houses}
                deviData={deviData}
                assignmentData={assignmentData}
                setAssignmentData={setAssignmentData}
                selectedProject={selectedProject}
                jobCodes={jobCodes}/>,
        },
        {
            title: "Prüfen / Abschluss",
            content:
                <FinalApproval
                    houses={houses}
                    assignmentData={assignmentData}
                    deviData={deviData}
                    jobCodes={jobCodes}
                    setIsGrouped={setIsGrouped}
                    setGroupedData={setGroupedData}
                    groupedData={groupedData}
                    selectedProject={selectedProject}
                />
        }
    ];

    useEffect(() => {
        if (selectedProject) {
            fetchData();
            fetchJobCodes(selectedProject);
        }
    }, [selectedProject]);

    const fetchData = async () => {
        try {
            const response = await axios.get<DeviData[]>(
                `${API_BASE_URL}/getNetworkPlanDeviPositions?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${selectedProject}&supplementNo=0`
            );
            setDeviData(response.data);
            setFilteredData(response.data);
        } catch (error) {
            message.error('Error fetching data');
        }
    };

    const fetchJobCodes = async (projectNo: string) => {
        try {
            const response = await axios.get<JobCode[]>(
                `${API_BASE_URL}/getNetworkPlanJobCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}`
            );
            setJobCodes(response.data);
        } catch (error) {
            console.error('Error fetching job codes:', error);
            message.error('Fehler beim Laden der JobCodes.');
        }
    };

    return (
        <div style={{ padding: "20px" }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                }}
            >
                <Tooltip title="Zurück">
                    <Button
                        icon={<LeftOutlined />}
                        onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
                        disabled={currentStep === 0}
                    />
                </Tooltip>
                <Steps current={currentStep} size="default" style={{ flex: 1, margin: "0 16px" }}>
                    {steps.map((step, index) => (
                        <Step key={index} title={step.title} />
                    ))}
                </Steps>
                {currentStep === steps.length - 1 ? (
                    <Tooltip title="Daten ins Abacus schreiben">
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            loading={isLoading}
                            disabled={!isGrouped}
                        >
                            {isLoading ? "Speichert..." : "Ins Abacus schreiben"}
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip title="Weiter">
                        <Button
                            icon={<RightOutlined />}
                            type="primary"
                            onClick={handleNext}
                            loading={isLoading}
                        />
                    </Tooltip>
                )}
            </div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#8c8c8c",
                    fontSize: 12,
                    marginBottom: 12,
                }}
            >
                <InfoCircleOutlined />
                <span>
                    Hinweis: Sonderzeichen wie &bdquo;&amp;&ldquo; in Bezeichnungen werden beim
                    Abacus-Export automatisch korrekt verarbeitet.
                </span>
            </div>
            <div>{steps[currentStep].content}</div>
        </div>
    );
};

export default CreateProjectWizard;
